import React from 'react';
import { format } from 'date-fns';
import VisitsCalendar from './VisitsCalendar';
import VisitsSummary from './VisitsSummary';
import VisitsTable from './VisitsTable';
import VisitsDurationChart from './VisitsDurationChart';
import VisitsStoresAnalysis from './VisitsStoresAnalysis';
import VisitsSalespeople from './VisitsSalespeople';
import VisitsTargets from './VisitsTargets';
import VisitsPlanned from './VisitsPlanned';
import VisitsMethodology from './VisitsMethodology';
import VisitsQuality from './VisitsQuality';
import VisitsMap from './VisitsMap';
import ErrorBoundary from '../ui/ErrorBoundary';
import { sourceOf } from '../../constants/visitsSource';
import { getAdminBadge } from '../../utils/getAdminBadge';

// Boolean, not the truthy chain: `{isadmin: false}` yielded `undefined`, which a
// prop default silently replaces. Fail closed at both ends.
const isAdminUser = (currentUser) => Boolean(currentUser?.isadmin || currentUser?.issuperadmin);

const icon = (path) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    {path}
  </svg>
);

/**
 * Visits tabs: id / label / icon (for the sidebar nav) + content (rendered by the
 * viewer for the active tab). Admin-only tabs are filtered out for non-admins.
 *
 * Order matters and is maintained by hand: the tabs everyone can see come first,
 * then the admin-only block (each carrying the shield on its label, so the badge is
 * stated once per tab rather than repeated on every indicator inside it), then the
 * Lexique last as the reference tab. Add a new tab to the right group.
 * The "Lexique" tab has no `requiresProject` and stands on its own.
 */
export const getVisitsTabs = ({
  universSplit = false,
  selectedUnivers,
  selectedProject,
  selectedShopSigns,
  visits,
  stats,
  startDate,
  endDate,
  selectedSalespeople,
  currentUser,
  fetchWithAuth,
  obfuscate,
  comparisonMode,
  showOutOfScope,
}) => {
  const dateRange = `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;

  const tabs = [
    {
      id: 'summary',
      label: 'Résumé',
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />),
      content: (
        <VisitsSummary
          universSplit={universSplit}
          selectedUnivers={selectedUnivers}
          visits={visits}
          stats={stats}
          username={
            !selectedSalespeople?.length
              ? 'Tous les vendeurs'
              : selectedSalespeople.length === 1
                ? selectedSalespeople[0].label
                : `${selectedSalespeople.length} vendeurs sélectionnés`
          }
          dateRange={dateRange}
          currentUser={currentUser}
          startDate={startDate}
          endDate={endDate}
          selectedProject={selectedProject}
          selectedShopSigns={selectedShopSigns}
          fetchWithAuth={fetchWithAuth}
          comparisonMode={comparisonMode}
        />
      ),
    },
    {
      id: 'planned',
      label: 'Visites planifiées',
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />),
      content: (
        <VisitsPlanned
          visits={visits}
          startDate={startDate}
          endDate={endDate}
          selectedProject={selectedProject}
          selectedShopSigns={selectedShopSigns}
          fetchWithAuth={fetchWithAuth}
          obfuscate={obfuscate}
        />
      ),
    },
    {
      id: 'calendar',
      label: 'Calendrier',
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />),
      content: (
        <VisitsCalendar
          visits={visits}
          startDate={startDate}
          endDate={endDate}
          obfuscate={obfuscate}
          currentUser={currentUser}
          showOutOfScope={showOutOfScope}
        />
      ),
    },
    {
      id: 'duration',
      label: 'Durées',
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />),
      content: <VisitsDurationChart visits={visits} stats={stats} selectedUnivers={selectedUnivers} />,
    },
    {
      id: 'targets',
      label: 'Objectifs',
      icon: icon(
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 12m-9 0a9 9 0 1 1 18 0a9 9 0 1 1-18 0" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 12m-6 0a6 6 0 1 1 12 0a6 6 0 1 1-12 0" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 12m-3 0a3 3 0 1 1 6 0a3 3 0 1 1-6 0" />
          <circle cx="12" cy="12" r="1" fill="currentColor" />
        </>
      ),
      content: (
        <VisitsTargets
          visits={visits}
          startDate={startDate}
          endDate={endDate}
          selectedProject={selectedProject}
          selectedShopSigns={selectedShopSigns}
          fetchWithAuth={fetchWithAuth}
        />
      ),
    },
    {
      id: 'stores',
      // Plain string: the label is a node (it carries the admin badge), and the
      // collapsed sidebar needs a tooltip it can render.
      title: 'Points de vente',
      label: <>{getAdminBadge()} Points de vente</>,
      admin: true,
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />),
      content: <VisitsStoresAnalysis visits={visits} dateRange={dateRange} selectedUnivers={selectedUnivers} />,
    },
    {
      id: 'salespeople',
      title: 'Force de vente',
      label: <>{getAdminBadge()} Force de vente</>,
      admin: true,
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />),
      content: (
        <VisitsSalespeople
          visits={visits}
          dateRange={dateRange}
          obfuscate={obfuscate}
          selectedUnivers={selectedUnivers}
        />
      ),
    },
    {
      id: 'table',
      title: 'Détails',
      label: <>{getAdminBadge()} Détails</>,
      admin: true,
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />),
      content: (
        <VisitsTable
          visits={visits}
          startDate={startDate}
          endDate={endDate}
          obfuscate={obfuscate}
          universSplit={universSplit}
        />
      ),
    },
    {
      id: 'quality',
      title: 'Contrôle qualité',
      // Badge porté par le libellé : tout l'onglet est réservé aux admins, donc les
      // vues qu'il contient ne rebadgent pas chacun de leurs indicateurs.
      label: <>{getAdminBadge()} Contrôle qualité</>,
      admin: true,
      // The measurements come from the TeamHaven answer tables; no other source
      // can produce them. Hidden rather than shown empty — on a control screen an
      // empty table reads as "no anomaly found".
      requiresQualitySource: true,
      icon: icon(<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />),
      content: (
        <VisitsQuality
          selectedProject={selectedProject}
          startDate={startDate}
          endDate={endDate}
          fetchWithAuth={fetchWithAuth}
          obfuscate={obfuscate}
        />
      ),
    },
    {
      id: 'map',
      title: 'Carte animée',
      label: <>{getAdminBadge()} Carte animée</>,
      admin: true,
      // Store coordinates come from a TeamHaven source table; no other source has
      // them. Hidden rather than shown empty — see the quality tab above.
      requiresGeoSource: true,
      icon: icon(
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </>
      ),
      // WebGL, an animation loop and a third-party map: exactly the kind of tab a
      // single bad frame could take the whole viewer down with.
      content: (
        <ErrorBoundary
          resetKeys={[selectedProject?.value, startDate, endDate]}
          fallback={(
            <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
              <p className="text-gray-600 dark:text-gray-400">
                La carte animée a rencontré une erreur. Les autres onglets restent utilisables.
              </p>
            </div>
          )}
        >
          <VisitsMap
            selectedProject={selectedProject}
            startDate={startDate}
            endDate={endDate}
            fetchWithAuth={fetchWithAuth}
            obfuscate={obfuscate}
            selectedSalespeople={selectedSalespeople}
            selectedShopSigns={selectedShopSigns}
            selectedUnivers={selectedUnivers}
          />
        </ErrorBoundary>
      ),
    },
    {
      id: 'lexicon',
      label: 'Lexique & Définitions',
      icon: icon(
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
        </>
      ),
      content: <VisitsMethodology isAdmin={isAdminUser(currentUser)} />,
    },
  ];

  // `supportsQuality` is undefined for a project selected from a pre-multi-source
  // link, which was always TeamHaven — so only an explicit false hides the tab.
  const qualityUnavailable = selectedProject?.supportsQuality === false;
  // The map reads DTL.TH."Targets", a TeamHaven table. `sourceOf` defaults to TH,
  // so a project selected from a pre-multi-source link keeps the tab.
  const geoUnavailable = sourceOf(selectedProject) !== 'TH';

  return tabs.filter((tab) => {
    if (tab.admin && !isAdminUser(currentUser)) return false;
    if (tab.requiresQualitySource && qualityUnavailable) return false;
    if (tab.requiresGeoSource && geoUnavailable) return false;
    return true;
  });
};
